import * as THREE from "three"
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js"

const PREVIEW_SIZE = { width: 1200, height: 900 }

const buildPreviewScene = () => {
  const scene = new THREE.Scene()
  scene.background = new THREE.Color("#eef4fb")

  const camera = new THREE.PerspectiveCamera(42, PREVIEW_SIZE.width / PREVIEW_SIZE.height, 0.1, 1000)
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true })
  renderer.setSize(PREVIEW_SIZE.width, PREVIEW_SIZE.height, false)
  renderer.setPixelRatio(1)
  renderer.outputColorSpace = THREE.SRGBColorSpace

  const hemiLight = new THREE.HemisphereLight("#ffffff", "#9cb3d1", 1.35)
  const dirLight = new THREE.DirectionalLight("#ffffff", 1.4)
  dirLight.position.set(8, 14, 10)
  scene.add(hemiLight, dirLight)

  const floor = new THREE.Mesh(
    new THREE.CircleGeometry(20, 64),
    new THREE.MeshStandardMaterial({
      color: "#dfe9f7",
      transparent: true,
      opacity: 0.88,
      roughness: 1
    })
  )
  floor.rotation.x = -Math.PI / 2
  floor.position.y = -0.02
  scene.add(floor)

  return { scene, camera, renderer }
}

const loadGltfScene = (sourceUrl) =>
  new Promise((resolve, reject) => {
    new GLTFLoader().load(sourceUrl, resolve, undefined, reject)
  })

const renderPreviewBlob = async (sourceUrl) => {
  const { scene, camera, renderer } = buildPreviewScene()

  try {
    const gltf = await loadGltfScene(sourceUrl)
    const model = gltf.scene
    scene.add(model)

    const box = new THREE.Box3().setFromObject(model)
    const size = box.getSize(new THREE.Vector3())
    const center = box.getCenter(new THREE.Vector3())
    const maxDimension = Math.max(size.x, size.y, size.z) || 1
    const distance = maxDimension * 1.6

    camera.position.set(center.x + distance, center.y + distance * 0.45, center.z + distance)
    camera.lookAt(center)
    camera.near = Math.max(distance / 100, 0.1)
    camera.far = distance * 50
    camera.updateProjectionMatrix()

    renderer.render(scene, camera)

    return await new Promise((resolve, reject) => {
      renderer.domElement.toBlob((result) => {
        if (result) {
          resolve(result)
          return
        }

        reject(new Error("Could not create preview image"))
      }, "image/png")
    })
  } finally {
    renderer.dispose()
  }
}

export const createPreviewFileFromGlbFile = async (file) => {
  const objectUrl = URL.createObjectURL(file)

  try {
    const blob = await renderPreviewBlob(objectUrl)

    return new File([blob], `${file.name.replace(/\.[^.]+$/, "")}-preview.png`, { type: "image/png" })
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

export const createPreviewFileFromGlbUrl = async (url, filename = "plan.glb") => {
  const response = await fetch(url, { credentials: "same-origin" })

  if (!response.ok) {
    throw new Error(`Could not fetch model file (${response.status})`)
  }

  const blob = await response.blob()
  const sourceFile = new File([blob], filename, { type: blob.type || "model/gltf-binary" })
  return createPreviewFileFromGlbFile(sourceFile)
}
